import despia from "despia-native";
import { isNativeShell } from "@/lib/oauthRedirect";

/**
 * One place that decides how we ask for push permission.
 *
 * Despia's check only reports enabled / not enabled, never "has the user been
 * asked yet". We tell the two apart with a local flag written the first time we
 * fire the native prompt, because iOS only shows that prompt once: after a
 * denial the only route back is the Settings app.
 */

export const PUSH_REQUESTED_KEY = "grounded_push_prompt_requested";
export const PUSH_PERMISSION_GRANTED_KEY = "grounded_push_permission_granted";

export type PushPermissionOutcome = "granted" | "requested" | "settings" | "unsupported";

function readFlag(key: string): boolean {
  try {
    return !!localStorage.getItem(key);
  } catch {
    return false;
  }
}

function writeFlag(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch { /* ignore */ }
}

/** True only when the native shell reports push as currently enabled. */
export async function readNativePushEnabled(): Promise<boolean> {
  try {
    const result = await despia("checkNativePushPermissions://", ["nativePushEnabled"]);
    const enabled = (result as { nativePushEnabled?: boolean | string } | null)?.nativePushEnabled;
    return enabled === true || enabled === "true";
  } catch {
    return false;
  }
}

/** Re-link the device to the signed in account after a fresh grant. */
export function bindPushIdentity(userId: string): void {
  if (!isNativeShell()) return;
  try {
    despia(`setonesignalplayerid://?user_id=${userId}`);
  } catch { /* ignore */ }
}

/**
 * Ask for push permission the right way for the user's current state:
 * never asked -> native popup, previously denied -> Settings, already on -> nothing.
 */
export async function requestPushPermission(): Promise<PushPermissionOutcome> {
  if (!isNativeShell()) return "unsupported";

  const enabled = await readNativePushEnabled();
  if (enabled) {
    writeFlag(PUSH_PERMISSION_GRANTED_KEY, "1");
    return "granted";
  }

  const alreadyAsked = readFlag(PUSH_REQUESTED_KEY);
  if (!alreadyAsked) {
    writeFlag(PUSH_REQUESTED_KEY, "1");
    try {
      despia("registerpush://");
      return "requested";
    } catch {
      // The bridge refused the prompt: fall back to the old Settings route so
      // the button always does something.
      try { despia("settingsapp://"); } catch { /* ignore */ }
      return "settings";
    }
  }

  try { despia("settingsapp://"); } catch { /* ignore */ }
  return "settings";
}

/**
 * After the native popup fires, check up to `tries` times, one second apart,
 * whether the person tapped Allow. False on decline or no answer in time.
 */
export async function waitForPushGrant(tries = 6, intervalMs = 1000): Promise<boolean> {
  for (let i = 0; i < tries; i++) {
    await new Promise((r) => setTimeout(r, intervalMs));
    if (await readNativePushEnabled()) {
      writeFlag(PUSH_PERMISSION_GRANTED_KEY, "1");
      return true;
    }
  }
  return false;
}
